import React, { useState } from 'react';
import { MoreVertical, Clock, XCircle, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

// Dropdown Menu Components (simplified)
function DropdownMenu({ children }) {
  return <div className="relative inline-block text-left">{children}</div>;
}

function DropdownMenuTrigger({ children, onClick }) {
  return <div onClick={onClick}>{children}</div>;
}

function DropdownMenuContent({ align = 'left', children, isOpen, onClose }) {
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-10" 
        onClick={onClose}
      />
      {/* Menu */}
      <div className={`
        absolute z-20 mt-2 w-48 rounded-lg bg-white dark:bg-gray-800 
        shadow-lg ring-1 ring-black ring-opacity-5 border border-gray-200 dark:border-gray-700
        ${align === 'right' ? 'right-0' : 'left-0'}
      `}>
        <div className="py-1">
          {children}
        </div>
      </div>
    </>
  );
}

function DropdownMenuItem({ children, onClick, className = '' }) {
  return (
    <button
      className={`
        w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 
        hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
        flex items-center gap-2
        ${className}
      `}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function DropdownMenuSeparator() {
  return <div className="h-px bg-gray-200 dark:bg-gray-700 my-1" />;
}

export function AlertCard({ alert, onDismiss, onSnooze }) {
  const [snoozed, setSnoozed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  async function snoozeAlert(duration) {
    try {
      await api.post('/api/v1/alert-preferences/snooze', {
        alert_type: alert.type,
        alert_id: alert.id,
        snooze_duration: duration
      });
      
      setSnoozed(true);
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      const durationText = hours > 0 
        ? `${hours} hour${hours > 1 ? 's' : ''}${minutes > 0 ? ` ${minutes}m` : ''}`
        : `${minutes} minute${minutes > 1 ? 's' : ''}`;
      toast.success(`Alert snoozed for ${durationText}`);
      
      if (onSnooze) onSnooze(alert);
    } catch (error) {
      console.error('Error snoozing alert:', error);
      toast.error('Failed to snooze alert');
    }
    setDropdownOpen(false);
  }

  async function dismissAlert() {
    try {
      await api.post('/api/v1/alert-preferences/dismiss', {
        alert_type: alert.type,
        alert_id: alert.id
      });
      
      setDismissed(true);
      toast.success('Alert dismissed');
      
      if (onDismiss) onDismiss(alert);
    } catch (error) {
      console.error('Error dismissing alert:', error);
      toast.error('Failed to dismiss alert');
    }
    setDropdownOpen(false);
  }

  // Don't render if snoozed or dismissed
  if (snoozed || dismissed) return null;

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          iconBg: 'bg-red-100 dark:bg-red-900/30',
          iconColor: 'text-red-600 dark:text-red-400',
          icon: AlertTriangle
        };
      case 'warning':
        return {
          bg: 'bg-amber-50 dark:bg-amber-900/20',
          border: 'border-amber-200 dark:border-amber-800',
          iconBg: 'bg-amber-100 dark:bg-amber-900/30',
          iconColor: 'text-amber-600 dark:text-amber-400',
          icon: AlertTriangle
        };
      case 'info':
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          iconBg: 'bg-blue-100 dark:bg-blue-900/30',
          iconColor: 'text-blue-600 dark:text-blue-400',
          icon: Info
        };
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-800/50',
          border: 'border-gray-200 dark:border-gray-700',
          iconBg: 'bg-gray-100 dark:bg-gray-800',
          iconColor: 'text-gray-600 dark:text-gray-400',
          icon: Info
        };
    }
  };

  const config = getSeverityConfig(alert.severity);
  const IconComponent = config.icon;

  return (
    <div className={`
      rounded-lg border p-4 transition-all hover:shadow-md
      ${config.bg} ${config.border}
    `}>
      <div className="flex items-start gap-4">
        <div className={`
          flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
          ${config.iconBg}
        `}>
          {alert.icon ? (
            <span className="text-2xl">{alert.icon}</span>
          ) : (
            <IconComponent className={`w-5 h-5 ${config.iconColor}`} />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
            {alert.title}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {alert.message}
          </p>
          {alert.created_at && (
            <p className="text-xs text-gray-500 dark:text-gray-500">
              {new Date(alert.created_at).toLocaleString()}
            </p>
          )}
        </div>
        
        {/* Alert Controls */}
        <DropdownMenu>
          <DropdownMenuTrigger onClick={() => setDropdownOpen(!dropdownOpen)}>
            <button className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="right" 
            isOpen={dropdownOpen}
            onClose={() => setDropdownOpen(false)}
          >
            <DropdownMenuItem onClick={() => snoozeAlert(60)}>
              <Clock className="w-4 h-4" />
              Snooze 1 hour
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => snoozeAlert(1440)}>
              <Clock className="w-4 h-4" />
              Snooze 24 hours
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => snoozeAlert(10080)}>
              <Clock className="w-4 h-4" />
              Snooze 1 week
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={dismissAlert} 
              className="text-red-600 dark:text-red-400"
            >
              <XCircle className="w-4 h-4" />
              Don't show again
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Action Link */}
      {alert.link && (
        <div className="mt-3">
          <a
            href={alert.link}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            View details →
          </a>
        </div>
      )}
    </div>
  );
}

export default AlertCard;